import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import OpenOrdersTable from '@/features/account/components/open-orders/open-orders-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useState } from 'react';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { OrderHistoryFilterType, OrderType, TableTabType } from '@/types';
import ExportOrdersHistory from '@/features/account/components/export-orders-history';
import PositionsTable from '@/features/account/components/positions/positions-table';
import OrdersHistoryTable from '@/features/account/components/orders-history-table';

export default function TradeTabs() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('view') as TableTabType;
  const isDefaultTabCorrect =
    !!defaultTab && Object.values(TableTabType).includes(defaultTab);

  const [tableTab, setTableTab] = useState<TableTabType>(
    isDefaultTabCorrect ? defaultTab : TableTabType.POSITIONS,
  );

  const [orderHistoryFilter, setOrderHistoryFilter] =
    useState<OrderHistoryFilterType>(OrderHistoryFilterType.ALL);

  const numberOfPositions = useCurrentAccount(
    (state) => Object.keys(state.positions).length,
  );

  const address = useAccountStore((state) => state.address);

  const openOrdersLength = useOrdersStore((state) => {
    const { orders, newOrderIds } = state.getOrders(address);
    return Array.from(newOrderIds).filter(
      (id) => orders[id] && orders[id].orderType !== OrderType.MARKET,
    ).length;
  });

  useEffect(() => {
    if (!isDefaultTabCorrect) return;
    setTableTab(defaultTab);
  }, [defaultTab]);

  return (
    <Tabs
      value={tableTab}
      onValueChange={(value) => setTableTab(value as TableTabType)}
      className="flex h-full w-full flex-col"
    >
      <div className="flex">
        <TabsList>
          <TabsTrigger className="gap-2 border-t-0" value="positions">
            {t('tradePage.positions')}
            <span className="text-vestgrey-100">
              {numberOfPositions > 0 ? `(${numberOfPositions})` : ''}
            </span>
          </TabsTrigger>
          <TabsTrigger className="gap-2 border-t-0" value="open-orders">
            {t('tradePage.openOrders')}
            <span className="text-vestgrey-100">
              {openOrdersLength > 0 ? `(${openOrdersLength})` : ''}
            </span>
          </TabsTrigger>
          <TabsTrigger className="border-t-0" value="order-history">
            {t('tradePage.orderHistory')}
          </TabsTrigger>
        </TabsList>
        <div className="flex w-full items-center justify-end gap-2 border-b border-border pr-2">
          {tableTab === TableTabType.ORDER_HISTORY && (
            <Select
              onValueChange={(value) =>
                setOrderHistoryFilter(value as OrderHistoryFilterType)
              }
            >
              <SelectTrigger id="filter" className="h-10 max-w-max">
                <SelectValue placeholder={t('tradePage.filter')} />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={OrderHistoryFilterType.ALL}>
                  {t('tradePage.all')}
                </SelectItem>
                <SelectItem value={OrderHistoryFilterType.FILLED}>
                  {t('tradePage.filled')}
                </SelectItem>
                <SelectItem value={OrderHistoryFilterType.REJECTED}>
                  {t('tradePage.rejected')}
                </SelectItem>
                <SelectItem value={OrderHistoryFilterType.CANCELLED}>
                  {t('tradePage.cancelled')}
                </SelectItem>
              </SelectContent>
            </Select>
          )}

          {tableTab === TableTabType.ORDER_HISTORY && (
            <ExportOrdersHistory filter={orderHistoryFilter} />
          )}
        </div>
      </div>
      <TabsContent className="relative min-h-48 flex-1" value="positions">
        <div className="absolute inset-0 h-full w-full">
          <PositionsTable />
        </div>
      </TabsContent>
      <TabsContent className="relative min-h-48 flex-1" value="open-orders">
        <div className="absolute inset-0 h-full w-full">
          <OpenOrdersTable />
        </div>
      </TabsContent>
      <TabsContent className="relative min-h-48 flex-1" value="order-history">
        <div className="absolute inset-0 h-full w-full">
          <OrdersHistoryTable filter={orderHistoryFilter} />
        </div>
      </TabsContent>
    </Tabs>
  );
}
